import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';

import { workspaceKeys } from '~teacher/hook/workspaces/workspaceQueryKeyFactory';

import { WorkspaceMembersPage } from '../../../api/workspaces/getWorkspaceMembers';
import { changeWorkspaceRole } from '../../../api/workspaces/permissions/changeRole/changeRole';

type MembersInfiniteData = InfiniteData<WorkspaceMembersPage>;
type CachedEntry = [readonly unknown[], MembersInfiniteData | undefined];

export const useChangeWorkspaceRole = (workspaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: changeWorkspaceRole,
    onMutate: ({ workspaceId, userId, role }) => {
      const entries = queryClient.getQueriesData<MembersInfiniteData>(
        workspaceKeys.members(workspaceId)
      ) as CachedEntry[];

      const snapshots: CachedEntry[] = [];
      for (const [key, data] of entries) {
        if (!data) continue;
        snapshots.push([key, data]);
        const next = produce(data, (draft) => {
          for (const page of draft.pages) {
            const index = page.members.findIndex((m) => m.id === userId);
            if (index !== -1) {
              page.members[index].role = role;
              break;
            }
          }
        });
        queryClient.setQueryData<MembersInfiniteData>(key, next);
      }

      return { snapshots };
    },
    onError: (_error, _variables, context) => {
      if (!context?.snapshots) return;
      for (const [key, data] of context.snapshots) {
        queryClient.setQueryData<MembersInfiniteData>(key, data);
      }
    },
    onSettled: () =>
      queryClient.invalidateQueries(workspaceKeys.members(workspaceId)),
  });
};
